// ENG-054: product threads in the widget, the pure part. No React, no storage and no imports, so
// the contract tests can pin it and the hook (pages/chat/useConversationThread.ts) stays small.
// ENG-055 adds the "Recent" list: registry v2 and the pinned thread. ENG-056: the bar's model (the
// open conversation and the "Recent" menu rows) and the general chat's title.
//
// In widget mode every product the host shows gets its own conversation thread (own n8n session,
// own persisted history) next to the general one. The general thread keeps the storage keys it had
// before threads existed, so existing conversations continue; a product thread adds the suffix
// "__<threadKey>". Nothing is shared between threads (tickets/.../product-threads/README.md).

export type ConversationThread = "general" | "product";

export type ConversationThreadState = {
    thread: ConversationThread;
    // "general", or "p:" + normSku(sku) for a product thread.
    threadKey: string;
    // The product of a product thread, as the host gave it (raw SKU); null in the general thread.
    productSku: string | null;
};

export const GENERAL_THREAD_KEY = "general";
export const PRODUCT_THREAD_KEY_PREFIX = "p:";
// Product threads kept per (site, user). Opening an 11th deletes the least recently opened one.
export const MAX_PRODUCT_THREADS = 10;
// Registry key prefix: `product_threads_${site}` inside the widget storage namespace.
export const PRODUCT_THREAD_REGISTRY_KEY_PREFIX = "product_threads_";

const SAFE_SKU = /^[A-Za-z0-9_-]{1,64}$/;

// 32-bit FNV-1a over the string's UTF-16 code units, as 8 hex digits.
// fnv1a32("") === "811c9dc5", fnv1a32("a") === "e40c292c", fnv1a32("foobar") === "bf9cf968".
export function fnv1a32(input: string): string {
    let hash = 0x811c9dc5;
    for (let i = 0; i < input.length; i++) {
        hash ^= input.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
    }
    return (hash >>> 0).toString(16).padStart(8, "0");
}

// A SKU made of 1-64 characters of [A-Za-z0-9_-] is used as is; anything else is "h" + its FNV-1a hex.
export function normSku(sku: string): string {
    return SAFE_SKU.test(sku) ? sku : "h" + fnv1a32(sku);
}

export function productThreadKey(sku: string): string {
    return PRODUCT_THREAD_KEY_PREFIX + normSku(sku);
}

export function isProductThreadKey(threadKey: string | null | undefined): threadKey is string {
    return typeof threadKey === "string" && threadKey.startsWith(PRODUCT_THREAD_KEY_PREFIX);
}

// Appended to each per-site storage key: n8n session, messages, last question, web citations.
// The general thread appends nothing, so its keys stay exactly as they were before ENG-054.
export function threadStorageSuffix(threadKey: string | null | undefined): string {
    return isProductThreadKey(threadKey) ? `__${threadKey}` : "";
}

// The thread on screen. Only the widget has product threads: a product thread when there is a
// thread product (ENG-055: the one picked from "Recent", else the host's) and the user has not
// chosen "General chat" for that same product. The standalone app always has the general thread only.
export function resolveConversationThread(
    isWidget: boolean,
    hostSku: string | null,
    generalChoiceSku: string | null,
    pinnedSku: string | null = null
): ConversationThreadState {
    const productSku = pinnedSku ?? hostSku;
    if (isWidget && productSku && generalChoiceSku !== productSku) {
        return { thread: "product", threadKey: productThreadKey(productSku), productSku };
    }
    return { thread: "general", threadKey: GENERAL_THREAD_KEY, productSku: null };
}

// ENG-055: what decides the thread on screen (the hook keeps it in a ref and in state).
export type ThreadSelection = {
    // The host's product as last applied; null when the host shows none.
    hostSku: string | null;
    // The product for which the user chose "General chat"; forgotten when the host's product changes.
    generalChoiceSku: string | null;
    // A product picked from "Recent". It is the thread's product instead of the host's until the
    // host shows a different product (or none) than it showed when the pin was made: pinHostSku.
    pinnedSku: string | null;
    pinHostSku: string | null;
};

export function initialThreadSelection(hostSku: string | null): ThreadSelection {
    return { hostSku, generalChoiceSku: null, pinnedSku: null, pinHostSku: null };
}

export function resolveThreadSelection(isWidget: boolean, selection: ThreadSelection): ConversationThreadState {
    return resolveConversationThread(isWidget, selection.hostSku, selection.generalChoiceSku, selection.pinnedSku);
}

// The host's product was read again: on its events, on navigation, and right before each send.
// Only a different product than before (than at the pin, while pinned) is a change: it forgets the
// pin and the general-chat choice, and the thread follows the host. The same product keeps both,
// and the same object comes back so the caller can tell nothing changed.
export function selectionAfterHostRead(selection: ThreadSelection, hostSku: string | null): ThreadSelection {
    const before = selection.pinnedSku !== null ? selection.pinHostSku : selection.hostSku;
    return hostSku === before ? selection : initialThreadSelection(hostSku);
}

// "Recent": the user picks the thread of `sku`. `hostSku` is the host's product read at that same
// moment, kept to tell a later host change apart. The host's own product needs no pin.
export function selectionAfterChooseRecent(sku: string, hostSku: string | null): ThreadSelection {
    return sku === hostSku ? initialThreadSelection(hostSku) : { hostSku, generalChoiceSku: null, pinnedSku: sku, pinHostSku: hostSku };
}

// "General chat": the general thread, for the thread's product (pinned or host's) only.
export function selectionAfterChooseGeneral(selection: ThreadSelection): ThreadSelection {
    const productSku = selection.pinnedSku ?? selection.hostSku;
    return productSku && selection.generalChoiceSku !== productSku ? { ...selection, generalChoiceSku: productSku } : selection;
}

// The thread's product from the general thread (ENG-056: its "Recent" row): back to it; a pin stays.
export function selectionAfterChooseProduct(selection: ThreadSelection): ThreadSelection {
    return selection.generalChoiceSku === null ? selection : { ...selection, generalChoiceSku: null };
}

// The host's product while another product is pinned (ENG-056: its "Recent" row): the host's product, without the pin.
export function selectionAfterChooseHost(selection: ThreadSelection): ThreadSelection {
    return selection.hostSku !== null && selection.pinnedSku !== null ? initialThreadSelection(selection.hostSku) : selection;
}

// ENG-056: the bar shows only the open conversation (a chip, not a button); every other place to
// go is a row of the "Recent (n)" menu, n = the number of rows.
export type ThreadMenuActive = { kind: "product"; sku: string } | { kind: "general" };

export type ThreadMenuItem =
    | { kind: "general" }
    | {
          kind: "product";
          sku: string;
          // Only a "recent" row has it: the title the registry remembers.
          title?: string;
          // "product": the thread's product from the general chat (selectionAfterChooseProduct);
          // "host": the host's product while another one is pinned (selectionAfterChooseHost);
          // "recent": any other product thread with messages (selectionAfterChooseRecent).
          action: "product" | "host" | "recent";
      };

// No bar at all (active: null) when the menu would be empty: the general chat on a page without
// a product and without recent product threads. A product thread always has the "General chat" row.
export type ThreadMenuModel = { active: ThreadMenuActive | null; items: ThreadMenuItem[] };

// Rows, in this order: the general chat (from a product thread); the thread's product (pinned,
// else the host's; from the general chat); the host's product while another one is pinned; then
// `recent` without the open thread and without the products already listed.
export function threadMenuModel(thread: ConversationThread, hostSku: string | null, pinnedSku: string | null, recent: RecentProductThread[]): ThreadMenuModel {
    const productSku = pinnedSku ?? hostSku;
    const inProduct = thread === "product" && productSku !== null;
    const items: ThreadMenuItem[] = [];
    const listed = new Set<string>();
    const addProduct = (sku: string, action: "product" | "host" | "recent", title?: string) => {
        listed.add(productThreadKey(sku));
        items.push({ kind: "product", sku, ...(title ? { title } : {}), action });
    };
    if (inProduct) {
        items.push({ kind: "general" });
        listed.add(productThreadKey(productSku));
    } else if (productSku !== null) addProduct(productSku, "product");
    if (pinnedSku !== null && hostSku !== null && hostSku !== pinnedSku) addProduct(hostSku, "host");
    for (const row of recent) {
        if (!listed.has(row.threadKey) && !listed.has(productThreadKey(row.sku))) addProduct(row.sku, "recent", row.title);
    }
    if (items.length === 0) return { active: null, items };
    return { active: inProduct ? { kind: "product", sku: productSku } : { kind: "general" }, items };
}

// ENG-056: the general chat is named by the user's last message in it: whitespace and line breaks
// become one space, trimmed, at most `max` characters and then "…"; "" when there is no message.
export const GENERAL_THREAD_LABEL_MAX = 40;
// ENG-058: the menu row of the general chat may take two lines, so it keeps more of the message.
export const GENERAL_THREAD_ROW_MAX = 90;
export const GENERAL_THREAD_TITLE_MAX = 200;

export function threadMessageText(lastQuestion: string | null | undefined, max: number): string {
    const text = typeof lastQuestion === "string" ? lastQuestion.replace(/\s+/g, " ").trim() : "";
    const chars = Array.from(text);
    return chars.length > max ? chars.slice(0, max).join("").trimEnd() + "…" : text;
}

// The chip and the menu row of the general chat (the chat icon, then the label); "Ask me" while it has no message (ENG-066).
// `max`: GENERAL_THREAD_LABEL_MAX for the chip (one line), GENERAL_THREAD_ROW_MAX for the menu row (ENG-058).
export function generalThreadLabel(lastQuestion: string | null | undefined, exampleLanguage: ThreadLanguage, max = GENERAL_THREAD_LABEL_MAX): string {
    return threadMessageText(lastQuestion, max) || threadCopy(exampleLanguage).askMe;
}

// README "Κοινό συμβόλαιο": only a product thread puts productSku (and productTitle) in
// host_context. The general thread never does, even when the host is on a product page.
export function threadHostProductSku(state: ConversationThreadState): string | null {
    return state.thread === "product" ? state.productSku : null;
}

// README "Κοινό συμβόλαιο": conversation_thread on every chat request; intro_suggestion: true only
// when the message was sent by a click on one of the intro's suggested questions. ENG-067: next to it,
// intro_suggestion_id, "<group id>.<question id>" of the clicked ready question (utils/readyQuestions.ts).
export function threadRequestFields(
    state: ConversationThreadState,
    introSuggestion: boolean,
    introSuggestionId?: string
): { conversation_thread: ConversationThread; intro_suggestion?: true; intro_suggestion_id?: string } {
    const intro = introSuggestion ? { intro_suggestion: true as const, ...(introSuggestionId ? { intro_suggestion_id: introSuggestionId } : {}) } : {};
    return { conversation_thread: state.thread, ...intro };
}

// ENG-055: the product-thread registry, `product_threads_${site}`, threadKey -> entry. v1 stored
// only the last-opened time, as a bare number. v2 keeps what the "Recent" list needs, since a
// hashed thread key cannot give the SKU back.
export type ProductThreadEntry = {
    // Last opened (epoch ms): decides which MAX_PRODUCT_THREADS threads are kept.
    lastUsed: number;
    // The product as the host gave it. Missing only in a v1 entry with a hashed key, until reopened.
    sku?: string;
    title?: string;
    // When the thread's stored messages last changed and were not empty (epoch ms); missing while it has none.
    lastMessageAt?: number;
};

export type ProductThreadRegistry = Record<string, ProductThreadEntry>;

// One row of the "Recent" list.
export type RecentProductThread = { threadKey: string; sku: string; title?: string };

export const RECENT_PRODUCT_THREADS_LIMIT = 5;

// normSku's hash form. A real SKU of exactly this shape looks the same, so it counts as a hash too.
const HASHED_SKU = /^h[0-9a-f]{8}$/;

const isTime = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

const cleanText = (value: unknown): string | undefined => (typeof value === "string" && value.trim() ? value.trim() : undefined);

// The SKU a product thread key holds as is; undefined for a hashed key, which cannot be reversed.
export function skuFromThreadKey(threadKey: string): string | undefined {
    if (!isProductThreadKey(threadKey)) return undefined;
    const sku = threadKey.slice(PRODUCT_THREAD_KEY_PREFIX.length);
    return SAFE_SKU.test(sku) && !HASHED_SKU.test(sku) ? sku : undefined;
}

// Any stored registry (v1, v2, a mix, or junk) as v2. A v1 number is { lastUsed } plus the SKU
// when the key holds it. v1 did not record messages either, so a v1 entry also gets
// lastMessageAt = lastUsed when `hasStoredMessages` says its history is not empty. A stored SKU
// that does not belong to its key is not trusted.
export function readProductThreadRegistry(registry: unknown, hasStoredMessages?: (threadKey: string) => boolean): ProductThreadRegistry {
    const out: ProductThreadRegistry = {};
    if (!registry || typeof registry !== "object" || Array.isArray(registry)) return out;
    for (const [threadKey, value] of Object.entries(registry as Record<string, unknown>)) {
        if (!isProductThreadKey(threadKey)) continue;
        const fields: Record<string, unknown> = isTime(value)
            ? { lastUsed: value }
            : value && typeof value === "object"
            ? (value as Record<string, unknown>)
            : {};
        if (!isTime(fields.lastUsed)) continue;
        const lastMessageAt = isTime(fields.lastMessageAt) ? fields.lastMessageAt : isTime(value) && hasStoredMessages?.(threadKey) ? value : undefined;
        const storedSku = cleanText(fields.sku);
        const sku = storedSku && productThreadKey(storedSku) === threadKey ? storedSku : skuFromThreadKey(threadKey);
        const title = cleanText(fields.title);
        out[threadKey] = {
            lastUsed: fields.lastUsed,
            ...(sku ? { sku } : {}),
            ...(title ? { title } : {}),
            ...(lastMessageAt !== undefined ? { lastMessageAt } : {})
        };
    }
    return out;
}

// Marks `threadKey` (the thread of `product.sku`) as opened `now` and keeps the `max` most recently
// opened product threads. Its title and lastMessageAt are kept; a given title replaces the title.
// Returns the registry to store and the thread keys that fell out: their messages, last question,
// web citations and session are deleted by the caller.
export function touchProductThread(
    registry: unknown,
    threadKey: string,
    now: number,
    product: { sku: string; title?: string },
    max: number = MAX_PRODUCT_THREADS
): { registry: ProductThreadRegistry; evicted: string[] } {
    const stored = readProductThreadRegistry(registry);
    const previous = stored[threadKey];
    const sku = productThreadKey(product.sku) === threadKey ? product.sku : previous?.sku ?? skuFromThreadKey(threadKey);
    const title = cleanText(product.title) ?? previous?.title;
    const entry: ProductThreadEntry = {
        lastUsed: now,
        ...(sku ? { sku } : {}),
        ...(title ? { title } : {}),
        ...(previous?.lastMessageAt !== undefined ? { lastMessageAt: previous.lastMessageAt } : {})
    };
    const others = Object.entries(stored)
        .filter(([key]) => key !== threadKey)
        .sort((a, b) => b[1].lastUsed - a[1].lastUsed);
    const room = Math.max(0, max - 1);
    return {
        registry: Object.fromEntries([[threadKey, entry], ...others.slice(0, room)]),
        evicted: others.slice(room).map(([key]) => key)
    };
}

// The thread's stored messages changed and are not empty. Only an existing entry is updated.
export function markProductThreadMessage(registry: unknown, threadKey: string, now: number): ProductThreadRegistry {
    const stored = readProductThreadRegistry(registry);
    const entry = stored[threadKey];
    return entry && isTime(now) ? { ...stored, [threadKey]: { ...entry, lastMessageAt: now } } : stored;
}

// The thread's stored messages became empty ("New chat", or a start without restore): it leaves "Recent".
export function clearProductThreadMessage(registry: unknown, threadKey: string): ProductThreadRegistry {
    const stored = readProductThreadRegistry(registry);
    const entry = stored[threadKey];
    if (!entry || entry.lastMessageAt === undefined) return stored;
    const { lastUsed, sku, title } = entry;
    return { ...stored, [threadKey]: { lastUsed, ...(sku ? { sku } : {}), ...(title ? { title } : {}) } };
}

// The chip or the intro learned the product's title. Only an existing entry is updated.
export function setProductThreadTitle(registry: unknown, threadKey: string, title: string): ProductThreadRegistry {
    const stored = readProductThreadRegistry(registry);
    const entry = stored[threadKey];
    const clean = cleanText(title);
    return entry && clean ? { ...stored, [threadKey]: { ...entry, title: clean } } : stored;
}

// "Recent": the product threads with a message and a known SKU, except `excludeThreadKey` (the
// thread on screen), most recent message first, at most `limit`. `activeSince` (epoch ms) leaves
// out threads not opened or written since then, whose stored history has expired (the registry
// itself has no TTL).
export function recentProductThreads(
    registry: unknown,
    excludeThreadKey: string | null | undefined,
    limit: number = RECENT_PRODUCT_THREADS_LIMIT,
    activeSince: number = -Infinity
): RecentProductThread[] {
    const rows: { threadKey: string; sku: string; title?: string; lastMessageAt: number; lastUsed: number }[] = [];
    for (const [threadKey, entry] of Object.entries(readProductThreadRegistry(registry))) {
        const { sku, title, lastMessageAt, lastUsed } = entry;
        if (threadKey === excludeThreadKey || sku === undefined || lastMessageAt === undefined) continue;
        if (Math.max(lastUsed, lastMessageAt) < activeSince) continue;
        rows.push({ threadKey, sku, title, lastMessageAt, lastUsed });
    }
    rows.sort((a, b) => b.lastMessageAt - a.lastMessageAt || b.lastUsed - a.lastUsed || (a.threadKey < b.threadKey ? -1 : 1));
    return rows.slice(0, Math.max(0, Math.floor(limit))).map(({ threadKey, sku, title }) => ({ threadKey, sku, ...(title ? { title } : {}) }));
}

// What the line shown after a thread switch says (not stored, not sent).
export type ThreadSwitchNotice = {
    to: ConversationThread;
    // The thread the user left had messages: they are kept and come back with that thread.
    previousSaved: boolean;
};

export type ThreadLanguage = "el" | "en";

// All ENG-054 UI text, Greek and English.
export const threadCopy = (exampleLanguage: ThreadLanguage) => {
    const el = exampleLanguage === "el";
    return {
        // ENG-066: "General chat" names the thread in the screen-reader switch notice and in the aria label with a
        // message; the visible label of the empty general chat is "Ask me", and its aria starts with that text.
        generalChat: el ? "Γενική συζήτηση" : "General chat",
        askMe: el ? "Ρώτησέ με" : "Ask me",
        askMeAria: el ? "Ρώτησέ με (γενική συζήτηση)" : "Ask me (general chat)",
        productChipAria: (name: string) => (el ? `Συζήτηση για το ${name}` : `Chat about ${name}`),
        aboutProductAria: (title: string) => (el ? `Επιστροφή στη συζήτηση για το ${title}` : `Back to the chat about ${title}`),
        nowAbout: (title: string) => (el ? `Συνέχεια για: ${title}` : `Now about: ${title}`),
        previousSaved: el ? " · η προηγούμενη συζήτηση κρατήθηκε" : " · your previous chat is saved",
        introAria: (title: string) => (el ? `Ξεκινήστε μια συζήτηση για το ${title}` : `Start a chat about ${title}`),
        introQuestion: (title: string) => (el ? `Θέλετε να μάθετε κάτι για το ${title};` : `Want to know anything about ${title}?`),
        introSuggestionsAria: el ? "Έτοιμες ερωτήσεις" : "Suggested questions",
        introSuggestionAria: (question: string) => (el ? `Ρωτήστε: ${question}` : `Ask: ${question}`),
        // ENG-067: the intro's questions themselves depend on the product type: utils/readyQuestions.ts.
        // ENG-055: the "Recent" menu and the pinned chip. ENG-056: the menu holds every other
        // conversation (general chat included), not only products.
        recent: (count: number) => (el ? `Πρόσφατα (${count})` : `Recent (${count})`),
        recentAria: (count: number) => (el ? `Πρόσφατες συζητήσεις: ${count}` : `Recent chats: ${count}`),
        recentMenuAria: el ? "Πρόσφατες συζητήσεις" : "Recent chats",
        recentItemAria: (title: string) => (el ? `Συνέχεια της συζήτησης για το ${title}` : `Continue the chat about ${title}`),
        aboutHostAria: (title: string) => (el ? `Μετάβαση στη συζήτηση για το ${title}` : `Switch to the chat about ${title}`),
        // ENG-056: the general chat's chip and row. ENG-068: the host's product row has no mark of its own.
        generalChatMessageAria: (message: string) => (el ? `Γενική συζήτηση: ${message}` : `General chat: ${message}`)
    };
};

// ENG-055/058: a product row of the "Recent" menu, and the product chip, start with the SKU, then the
// title. `sku` is the part shown in its own colour; `label` is the title, or the SKU itself (once) when there is none. A
// title equal to the SKU is no title: the lookups fall back to the SKU when nothing is known.
export function productRowParts(sku: string, title?: string): { sku?: string; label: string } {
    const name = (title ?? "").trim();
    return name && name !== sku ? { sku, label: name } : { label: sku };
}

// ENG-058/068: "{sku} · {title}", or the SKU alone: the tooltip and the name in the aria label of a row or the chip.
export function productRowText(sku: string, title?: string): string {
    const { sku: shown, label } = productRowParts(sku, title);
    return shown ? `${shown} · ${label}` : label;
}

// "Now about: {title}" for a product thread, "General chat" for the general one, plus
// " · your previous chat is saved" when the thread the user left had messages.
export function threadSwitchNoticeText(exampleLanguage: ThreadLanguage, notice: ThreadSwitchNotice, productTitle: string): string {
    const copy = threadCopy(exampleLanguage);
    const head = notice.to === "product" ? copy.nowAbout(productTitle) : copy.generalChat;
    return head + (notice.previousSaved ? copy.previousSaved : "");
}

const toPrice = (value: unknown): number | undefined => {
    const n = typeof value === "number" ? value : typeof value === "string" && value.trim() ? Number(value) : NaN;
    return Number.isFinite(n) && n > 0 ? n : undefined;
};

// The intro's price from GET /product_details `pricing` (shape: gateway services/products.py,
// read the same way as components/Cards/ProductCard.tsx): the chosen source's sale price when
// on sale, with its list price to strike through, otherwise its list price.
export function introPrice(pricing: unknown, priceSource: "web" | "store"): { price: number; listPrice?: number } | undefined {
    if (!pricing || typeof pricing !== "object") return undefined;
    const all = pricing as Record<string, any>;
    const side: Record<string, any> = all[priceSource] && typeof all[priceSource] === "object" ? all[priceSource] : all;
    const list = toPrice(side.listPrice);
    const sale = toPrice(side.salePrice);
    const onSale = sale !== undefined && (typeof side.hasSalePrice === "boolean" ? side.hasSalePrice : list !== undefined && sale < list);
    if (onSale) return { price: sale, ...(list !== undefined && list > sale ? { listPrice: list } : {}) };
    const price = list ?? sale;
    return price === undefined ? undefined : { price };
}
